import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { IGetApiType } from '../../../REST';
import { IChartData, IParamsList, ITariffPackageInstanceStatisticItem, SelectType } from '../../../interfaces';
import { useTariffPackageInstanceItem } from '../../../hooks';
import { UnitTypeValues, getTariffPackageInstancesStatistics } from '../../../middleware';
import { mapApi2UiField, smsHumanFormat, timeHumanFormat } from '../../../instruments';
import { tI, tP } from '../../../translate';
import {
    CardLayout,
    Catcher,
    FilterOriginalUnitTypes,
    FilterPageSize,
    cardLayoutOptions,
    showDataBytesText,
} from '../../../Components';
import { HorizontalUsageChart } from '../../Dashboard/Components';
import DashboardStyles from '../../Dashboard/m_dashboarStyles.less';
import ViewStyles from '../../styles/m_viewStyles.less';

function getTopItems(items: ITariffPackageInstanceStatisticItem[], top: number): ITariffPackageInstanceStatisticItem[] {
    if (items.length === 0) {
        return [];
    }
    const sorted: ITariffPackageInstanceStatisticItem[] = items.sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0));
    if (sorted.length <= top) {
        return sorted;
    }
    const topItems: ITariffPackageInstanceStatisticItem[] = sorted.slice(0, top);
    const restSum = sorted.slice(top).reduce((sum, item) => sum + (item.volume ?? 0), 0);
    return [
        ...topItems,
        {
            name: 'Others',
            volume: restSum,
        },
    ];
}

export const ShowTariffPackageInstanceStatistics: React.FC = observer(() => {
    const loadingTypes: IGetApiType[] = ['GET_TARIFF_PACKAGE_INSTANCE_STATISTICS'];
    const [list, setList] = useState<ITariffPackageInstanceStatisticItem[]>([]);
    const [top, setTop] = useState<number>(5);
    const [unitType, setUnitType] = useState<UnitTypeValues>(UnitTypeValues.DATA);
    const { id, startDate, endDate } = useTariffPackageInstanceItem();

    const onChangeUnitTypes = (unitTypes: any): void => {
        setUnitType(unitTypes);
    };

    const getList = async (id: string, searchParams?: IParamsList): Promise<void> => {
        const list = await getTariffPackageInstancesStatistics(id, searchParams);
        setList(list.map((item) => ({ ...mapApi2UiField(item) })));
    };

    useEffect(() => {
        if (id) {
            getList(id.toString(), { startDate, endDate, unitType });
        }
    }, [id, startDate, endDate, unitType]);

    const onChangeTop = (top?: number): void => {
        setTop(top ?? 5);
    };

    const chartData2: IChartData =
        getTopItems(list, top)?.map((item) => {
            const title = `${item.name || '# ' + item.cardId?.toString()}`;
            const showUnit =
                unitType === UnitTypeValues.DATA
                    ? showDataBytesText({ bytes: item.volume, isHumanView: true })
                    : unitType === UnitTypeValues.SMS
                    ? smsHumanFormat(item.volume)
                    : timeHumanFormat(item.volume);

            return [title, Number(item.volume), showUnit, `${item.volume} ${unitType}`];
        }) || [];

    chartData2.unshift([tP('Card'), tI('Usage'), { role: 'annotation' }, { role: 'annotationText' }]);

    return (
        <Catcher>
            <CardLayout
                columnOption={cardLayoutOptions.cardInfoEdit}
                idSpinners={loadingTypes}
                title={'Statistics'}
                titleToolBar={
                    <div className={cx(ViewStyles.combinedToolBar, ViewStyles.cardToolBar)}>
                        <div className={ViewStyles.combinedFilterItem}>
                            <FilterOriginalUnitTypes
                                filterParams={{ unitTypes: SelectType.SINGLE, defaultUnitTypes: unitType }}
                                isBlockAllowClear
                                isUseVisible
                                onChange={onChangeUnitTypes}
                                value={unitType}
                            />
                        </div>
                        {list.length > 5 ? (
                            <div className={cx(ViewStyles.combinedFilterItem, ViewStyles.combinedFilterPageSize)}>
                                <FilterPageSize
                                    onChange={onChangeTop}
                                    isLocalChange
                                    isSelectOnly
                                    value={top}
                                    title={'Top'}
                                />
                            </div>
                        ) : null}
                    </div>
                }
            >
                {/*<div style={{ width: 500, height: 400 }}>*/}
                <div className={DashboardStyles.chart}>
                    <HorizontalUsageChart sourceData={chartData2} unitType={unitType} />
                </div>
                {/*</div>*/}
            </CardLayout>
        </Catcher>
    );
});
